import type {
  ContactChannel,
  ContractContactAlertType,
  ContractContactResource,
  OccupantContactMethod,
} from "../types";
import type { OccupantFormValues } from "../hooks/use-occupant-form-schema";

/**
 * Maps the drawer onto the payload the API is being asked to accept: a contact
 * owns a LIST of phone numbers and a LIST of email addresses, and each entry
 * carries the alert types it serves.
 *
 * The old `phone` / `phone2` / `phone3` columns can't express this — three
 * fixed slots, with the alert type implied by which slot a number sits in
 * (`sms → phone3`, `call → phone`). A contact with two SMS numbers, or a second
 * email, has nowhere to go. So the frontend sends the real shape and the
 * backend grows to match it; this file is the whole of that contract.
 */

/** One destination as the drawer writes it: the number or address, what it is
 * called, and what it is alerted on. Stricter than the resource's own
 * {@link OccupantContactMethod} — the form always has a label string, even if
 * it is empty. */
export interface OccupantContactMethodInput {
  label: string;
  value: string;
  alert_types: ContractContactAlertType[];
}

export interface OccupantPayload {
  contact: {
    first_name: string;
    last_name: string;
    is_primary: boolean;
    phones: OccupantContactMethodInput[];
    emails: OccupantContactMethodInput[];
  };
}

/** Drops the rows the user left blank and keeps the alert types each one carries. */
const toPhoneMethods = (phones: OccupantFormValues["phones"]): OccupantContactMethodInput[] =>
  phones
    .filter((phone) => phone.value !== "")
    .map((phone) => ({
      label: phone.label,
      value: phone.value,
      alert_types: [...(phone.alert_call ? (["call"] as const) : []), ...(phone.alert_sms ? (["sms"] as const) : [])],
    }));

const toEmailMethods = (emails: OccupantFormValues["emails"]): OccupantContactMethodInput[] =>
  emails
    .filter((email) => email.value !== "")
    .map((email) => ({
      label: email.label,
      value: email.value,
      alert_types: email.alert_email ? (["email"] as const).slice() : [],
    }));

export const occupantPayload = (values: OccupantFormValues): OccupantPayload => ({
  contact: {
    first_name: values.first_name,
    last_name: values.last_name,
    is_primary: values.is_primary,
    phones: toPhoneMethods(values.phones),
    emails: toEmailMethods(values.emails),
  },
});

/** One subscription the contract should hold: an alert type on a destination. */
export interface OccupantSubscription {
  alertType: ContractContactAlertType;
  destination: string;
}

/**
 * Flattens the drawer's destinations into the subscriptions they imply — one
 * per (destination, alert type). Two numbers both ticked for SMS give two SMS
 * rows, which is exactly what the fixed columns could not hold.
 */
export const occupantSubscriptions = (values: OccupantFormValues): OccupantSubscription[] => {
  const { phones, emails } = occupantPayload(values).contact;

  return [...phones, ...emails].flatMap((method) =>
    method.alert_types.map((alertType) => ({ alertType, destination: method.value })),
  );
};

/**
 * Every way a contact can be reached, as the API serves it: the `phones` /
 * `emails` arrays once the backend has them, the legacy single-value columns
 * until then.
 */
export interface ContactMethodAttributes {
  /** The destinations as the backend owns them, ids included. */
  channels?: ContactChannel[] | null;
  phones?: OccupantContactMethod[] | null;
  emails?: OccupantContactMethod[] | null;
  phone?: string | null;
  phone2?: string | null;
  phone3?: string | null;
  email?: string | null;
}

/**
 * Which alert types are live on each destination, keyed both ways: by the
 * channel id an alert row points at, and by the raw destination string for the
 * rows that predate channels (`contact_channel_id: null`). A destination's ticks
 * cannot be read off `phones[].alert_types` — the backend only fills that in for
 * channel-linked rows, so a legacy occupant comes back with alert rows and an
 * empty `alert_types`, which is exactly the case that showed up unticked.
 */
interface AlertTicks {
  byChannelId: Map<string, Set<ContractContactAlertType>>;
  byDestination: Map<string, Set<ContractContactAlertType>>;
}

const addTick = (
  index: Map<string, Set<ContractContactAlertType>>,
  key: string | number | null | undefined,
  alertType: ContractContactAlertType | undefined,
) => {
  if (!key || !alertType) {
    return;
  }

  const ticks = index.get(String(key)) ?? new Set<ContractContactAlertType>();

  ticks.add(alertType);
  index.set(String(key), ticks);
};

/**
 * Reads the ticks out of an occupant's alert rows. Both the top-level rows and
 * the copies nested on the contact are folded in: the nested list is the
 * contact's full alert picture, so it still resolves a channel whose own row
 * fell outside what was fetched.
 */
export const alertTicksFrom = (contractContacts: ContractContactResource[]): AlertTicks => {
  const ticks: AlertTicks = { byChannelId: new Map(), byDestination: new Map() };

  for (const contractContact of contractContacts) {
    const { contact_channel_id, alert_destination, alert_type } = contractContact.attributes;

    addTick(ticks.byChannelId, contact_channel_id, alert_type);
    addTick(ticks.byDestination, alert_destination, alert_type);

    for (const alert of contractContact.relationships?.contact?.data?.attributes?.contract_contact_alerts ?? []) {
      addTick(ticks.byChannelId, alert.contact_channel_id, alert.alert_type);
    }
  }

  return ticks;
};

const NO_TICKS: AlertTicks = { byChannelId: new Map(), byDestination: new Map() };

/** A destination's ticks: by its channel id when it has one, otherwise by the
 * value the alert row delivered to. */
const ticksFor = (ticks: AlertTicks, channelId: string | number | undefined, value: string) =>
  (channelId !== undefined ? ticks.byChannelId.get(String(channelId)) : undefined) ??
  ticks.byDestination.get(value) ??
  new Set<ContractContactAlertType>();

/** A destination as the drawer lists it, before the alert boxes are resolved. */
interface DestinationEntry {
  channelId?: string | number;
  label: string;
  value: string;
}

/**
 * Every destination on file, in the order the drawer should list them. Read from
 * `channels` — the record the backend actually owns, ids included — and only
 * falling back to the `phones` / `emails` arrays, then to the legacy single-value
 * columns, for a contact served the old way.
 */
const destinationEntries = (contact: ContactMethodAttributes | undefined) => {
  if (contact?.channels?.length) {
    const sorted = [...contact.channels].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const entries = (type: ContactChannel["type"]): DestinationEntry[] =>
      sorted
        .filter((channel) => channel.type === type)
        .map((channel) => ({ channelId: channel.id, label: channel.label ?? "", value: channel.value }));

    return { phones: entries("phone"), emails: entries("email") };
  }

  const fromMethods = (methods: OccupantContactMethod[] | null | undefined): DestinationEntry[] | null =>
    methods ? methods.map((method) => ({ label: method.label ?? "", value: method.value })) : null;

  const fallback: { phones: DestinationEntry[]; emails: DestinationEntry[] } = {
    phones:
      fromMethods(contact?.phones) ??
      [contact?.phone, contact?.phone3, contact?.phone2]
        .filter((value): value is string => Boolean(value))
        // The legacy columns carried no label of their own.
        .map((value) => ({ label: "", value })),
    emails: fromMethods(contact?.emails) ?? (contact?.email ? [{ label: "", value: contact.email }] : []),
  };

  return fallback;
};

/**
 * Turns a contact's destinations into editable form rows, with each row's alert
 * boxes ticked from the subscriptions that deliver to it. Pass no rows for a
 * contact that has no subscription on this contract yet, so every box starts
 * unticked.
 */
export const contactMethodRows = (
  contact: ContactMethodAttributes | undefined,
  contractContacts: ContractContactResource[] = [],
) => {
  const ticks = contractContacts.length > 0 ? alertTicksFrom(contractContacts) : NO_TICKS;
  const { phones: phoneEntries, emails: emailEntries } = destinationEntries(contact);

  const phones = phoneEntries
    .filter((entry) => entry.value !== "")
    .map((entry) => {
      const alertTypes = ticksFor(ticks, entry.channelId, entry.value);

      return {
        label: entry.label,
        value: entry.value,
        alert_call: alertTypes.has("call"),
        alert_sms: alertTypes.has("sms"),
      };
    });

  const emails = emailEntries
    .filter((entry) => entry.value !== "")
    .map((entry) => ({
      label: entry.label,
      value: entry.value,
      alert_email: ticksFor(ticks, entry.channelId, entry.value).has("email"),
    }));

  return {
    // A blank row so there is always somewhere to type.
    phones: phones.length > 0 ? phones : [{ label: "", value: "", alert_call: false, alert_sms: false }],
    emails: emails.length > 0 ? emails : [{ label: "", value: "", alert_email: false }],
  };
};

/**
 * The whole drawer state for an existing occupant, read from the rows the drawer
 * fetched: the person, their destinations, the alert types each one carries and
 * whether they are the client's primary contact. Nothing is lifted from the
 * table, so what is edited is what the server currently holds.
 */
export const occupantFormValuesFrom = (contractContacts: ContractContactResource[]): Partial<OccupantFormValues> => {
  const contact = contractContacts[0]?.relationships?.contact?.data;

  return {
    contactId: String(contact?.id ?? ""),
    first_name: contact?.attributes?.first_name ?? "",
    last_name: contact?.attributes?.last_name ?? "",
    ...contactMethodRows(contact?.attributes, contractContacts),
    // `contacts.is_primary` comes back null on this resource; the row's
    // `is_primary_contact` is the field that answers it.
    is_primary: contractContacts.some((contractContact) => contractContact.attributes.is_primary_contact),
  };
};

/** The occupant's display name, for the drawer header. */
export const occupantName = (contractContacts: ContractContactResource[]) => {
  const contact = contractContacts[0]?.relationships?.contact?.data?.attributes;

  return [contact?.first_name, contact?.last_name].filter(Boolean).join(" ").trim();
};
